import React, { useState } from "react";
import SimpleReactValidator from "simple-react-validator";
import { submitContactForm } from "../../services/api";
import { getAttribution } from "../ReferralTracker/ReferralTracker";
import { track } from "../../services/analytics";

// Import icons
import userIcon from "../../images/icon/user-balck-icon.svg";
import emailIcon from "../../images/icon/sms-balck-icon.svg";
import phoneIcon from "../../images/icon/call-icon02.svg";
import uploadIcon from "../../images/icon/upload-icon.svg";
import listIcon from "../../images/icon/list-icon.svg";
import messageIcon from "../../images/icon/messages-icon.svg";
import arrowIcon from "../../images/icon/rotate-arrow-black02.svg";

const ContactForm: React.FC = () => {
  const [forms, setForms] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    inquiry_type: "",
    message: "",
    file: null as File | null,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [validator] = useState(
    new SimpleReactValidator({ className: "errorMessage" })
  );

  const changeHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForms({ ...forms, [name]: value });

    if (validator.allValid()) validator.hideMessages();
    else validator.showMessages();
  };

  const fileHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setForms({ ...forms, file });
  };

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (validator.allValid()) {
      setLoading(true);

      try {
        // Submit to own backend API at api.axoix.in
        await submitContactForm({
          name: forms.name,
          email: forms.email,
          phone: forms.phone || undefined,
          subject: forms.subject,
          message: `[${forms.inquiry_type}] ${forms.message}`,
          source: "contact_page",
          ...getAttribution(),
        });

        setSuccess(true);
        track("generate_lead", { source: "contact_page" });
        setForms({
          name: "",
          email: "",
          phone: "",
          subject: "",
          inquiry_type: "",
          message: "",
          file: null,
        });
        validator.hideMessages();

        // Reset success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);

      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : "Failed to submit.";
        const mailtoSubject = encodeURIComponent(`[AXOIX Contact] ${forms.inquiry_type} - ${forms.subject}`);
        const mailtoBody = encodeURIComponent(`Name: ${forms.name}\nEmail: ${forms.email}\nPhone: ${forms.phone || "N/A"}\n\n${forms.message}`);
        setError(`${errorMessage} You can also email us directly at <a href="mailto:hello@axoix.com?subject=${mailtoSubject}&body=${mailtoBody}" style="color: var(--color-primary)">hello@axoix.com</a>`);
      } finally {
        setLoading(false);
      }
    } else {
      validator.showMessages();
    }
  };

  if (success) {
    return (
      <div className="text-center" style={{ padding: "40px 20px" }}>
        <span style={{ fontSize: "60px", display: "block", marginBottom: "20px" }}>✅</span>
        <h3 className="title mb-15">Message Sent Successfully!</h3>
        <p className="clr-white">
          Thank you for contacting us. Our team will review your message and get back to you within 24-48 hours.
        </p>
        <p className="clr-white mt-15" style={{ opacity: 0.7, fontSize: "14px" }}>
          A confirmation has been sent to your email address.
        </p>
        <button
          onClick={() => setSuccess(false)}
          className="thm-btn form-btn mt-30"
        >
          Send Another Message
          <span className="xb-icon">
            <img src={arrowIcon} alt="arrow" />
            <img src={arrowIcon} alt="arrow" />
          </span>
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submitHandler} className="xb-contact-input-form">
      {error && (
        <div className="services-outcome-wrap mb-30" style={{ borderColor: "#ff4444" }}>
          <p style={{ color: "#ff4444", margin: 0 }} dangerouslySetInnerHTML={{ __html: `<i class="fas fa-exclamation-circle"></i> ${error}` }} />
        </div>
      )}

      <div className="row mt-none-20">
        {/* Name */}
        <div className="col-lg-6 col-md-6 mt-20">
          <div className="xb-input-field">
            <input
              id="contact-name"
              name="name"
              type="text"
              value={forms.name}
              onChange={changeHandler}
              required
            />
            <label htmlFor="contact-name">Your Name*</label>
            <img src={userIcon} alt="user" />
          </div>
          {validator.message("name", forms.name, "required|alpha_space")}
        </div>

        {/* Email */}
        <div className="col-lg-6 col-md-6 mt-20">
          <div className="xb-input-field">
            <input
              id="contact-email"
              name="email"
              type="email"
              value={forms.email}
              onChange={changeHandler}
              required
            />
            <label htmlFor="contact-email">Email Address*</label>
            <img src={emailIcon} alt="email" />
          </div>
          {validator.message("email", forms.email, "required|email")}
        </div>

        {/* Phone */}
        <div className="col-lg-6 col-md-6 mt-20">
          <div className="xb-input-field">
            <input
              id="contact-phone"
              name="phone"
              type="text"
              value={forms.phone}
              onChange={changeHandler}
            />
            <label htmlFor="contact-phone">Phone Number (Optional)</label>
            <img src={phoneIcon} alt="phone" />
          </div>
        </div>

        {/* Subject */}
        <div className="col-lg-6 col-md-6 mt-20">
          <div className="xb-input-field">
            <input
              id="contact-subject"
              name="subject"
              type="text"
              value={forms.subject}
              onChange={changeHandler}
              required
            />
            <label htmlFor="contact-subject">Subject*</label>
            <img src={listIcon} alt="subject" />
          </div>
          {validator.message("subject", forms.subject, "required")}
        </div>

        {/* Inquiry Type */}
        <div className="col-lg-6 col-md-6 mt-20">
          <div className="xb-input-field xb-select-field">
            <select
              name="inquiry_type"
              value={forms.inquiry_type}
              onChange={changeHandler}
              required
              className="nice-select"
            >
              <option value="">Select Inquiry Type*</option>
              <option value="general">General Inquiry</option>
              <option value="support">Technical Support</option>
              <option value="billing">Billing Question</option>
              <option value="complaint">Complaint</option>
              <option value="feedback">Feedback / Suggestion</option>
              <option value="partnership">Partnership Inquiry</option>
              <option value="other">Other</option>
            </select>
            <img src={listIcon} alt="list" />
          </div>
          {validator.message("inquiry_type", forms.inquiry_type, "required")}
        </div>

        {/* File Upload */}
        <div className="col-lg-6 col-md-6 mt-20">
          <div className="xb-input-field xb-select-file">
            <input
              type="file"
              onChange={fileHandler}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              aria-label="Attach a file (optional)"
            />
            <img src={uploadIcon} alt="upload" />
            <span>{forms.file ? forms.file.name : "Attach file (Optional)"}</span>
          </div>
        </div>

        {/* Message */}
        <div className="col-lg-12 col-md-12 mt-20">
          <div className="xb-input-field xb-massage-field">
            <textarea
              id="contact-message"
              name="message"
              value={forms.message}
              onChange={changeHandler}
              required
              rows={5}
            ></textarea>
            <label htmlFor="contact-message">How can we help you?*</label>
            <img src={messageIcon} alt="message" />
          </div>
          {validator.message("message", forms.message, "required")}
        </div>
      </div>

      {/* Submit Button */}
      <div className="form-submit-btn mt-35">
        <button type="submit" className="thm-btn form-btn" disabled={loading}>
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: "10px" }}></i>
              Sending...
            </>
          ) : (
            <>
              Send Message
              <span className="xb-icon">
                <img src={arrowIcon} alt="arrow" />
                <img src={arrowIcon} alt="arrow" />
              </span>
            </>
          )}
        </button>
      </div>

      <p className="clr-white mt-20" style={{ fontSize: "13px", opacity: 0.7, textAlign: "center" }}>
        By submitting this form, you agree to our{" "}
        <a href="/privacy" style={{ color: "var(--color-primary)" }}>Privacy Policy</a>.
        We typically respond within 24-48 hours.
      </p>
    </form>
  );
};

export default ContactForm;
